(ns common.schema.entity-creator.utils
  (:require [clojure.set :as set]
            [common.permissions.field-permissions :as field-permissions]
            [common.roles :as roles]
            [common.schema.field-spec :as field-spec]
            [common.schema.registry :as registry]
            [common.util :as cu]))

(defn all-fields
  [{:keys [tenant tenant-schema]} entity-type]
  (let [tenant tenant
        blacklisted (set (:tenant/blacklisted-fields tenant))
        whitelisted (set (:tenant/whitelisted-custom-fields tenant))
        ;; remove blacklisted unless also explicitly whitelisted
        blacklisted (set/difference blacklisted whitelisted)]
    (apply dissoc
           (registry/get-schema tenant-schema entity-type)
           blacklisted)))

(defn mandatory?
  [[_k {:field/keys [default-visibility] :as field-schema}]]
  (and (field-spec/system-required-or-legacy-always-mandatory-field? field-schema)
       (->> default-visibility :field-visibility (= :in-use-disabled))))

(def visible-settings
  #{:mandatory :system-mandatory :optional})

;; TODO new key(s) to field-schema to handle these? e.g. :field/system-field
;; (some analogous fields are handled by :role/system write-access).
;;  won't change now to avoid regression bugs
(def fields-handled-elsewhere
  {:entity.type/named-value
   #{:named-value/attribute}
   :entity.type/pipeline
   #{:pipeline/checklist-configurations}
   :entity.type/task
   #{:task/resolve-status}
   :entity.type/oppo
   #{:oppo/pipeline
     :oppo/stage
     :oppo/status
     :oppo/target-asset
     :oppo/flagged-by
     :oppo/on-hold-until
     :oppo/pass-reason-v2
     :oppo/last-hold-comment}
   :entity.type/company
   #{}
   :entity.type/investor
   #{:investor/legal-entity
     :investor/name}
   :entity.type/exit-opportunity
   #{:exit-opportunity/status
     :exit-opportunity/buyer
     :exit-opportunity/pipeline
     :exit-opportunity/stage
     :exit-opportunity/on-hold-comment
     :exit-opportunity/on-hold-until
     :exit-opportunity/put-on-hold-on
     :exit-opportunity/won-on
     :exit-opportunity/set-as-won-by
     :exit-opportunity/set-as-lost-by
     :exit-opportunity/lost-comment
     :exit-opportunity/lost-on
     :exit-opportunity/lost-reason}
   :entity.type/sales-opportunity
   #{:sales-opportunity/customer
     :sales-opportunity/stage
     :sales-opportunity/pipeline
     :sales-opportunity/status
     :sales-opportunity/put-on-hold-on
     :sales-opportunity/on-hold-comment
     :sales-opportunity/on-hold-until
     :sales-opportunity/won-on
     :sales-opportunity/set-as-won-by
     :sales-opportunity/lost-comment
     :sales-opportunity/lost-on
     :sales-opportunity/lost-reason}
   :entity.type/fundraising-opportunity
   #{:fundraising-opportunity/investor
     :fundraising-opportunity/opportunity-stage
     :fundraising-opportunity/pipeline
     :fundraising-opportunity/status
     :fundraising-opportunity/put-on-hold-on
     :fundraising-opportunity/on-hold-comment
     :fundraising-opportunity/on-hold-until
     :fundraising-opportunity/won-on
     :fundraising-opportunity/set-as-won-by
     :fundraising-opportunity/lost-comment
     :fundraising-opportunity/lost-on
     :fundraising-opportunity/lost-reason}})

(def person-name-and-email-attributes
  "Protected person identity fields. They always appear first in person
   creators and creator configuration, cannot be hidden or removed, and share
   the at-least-one fallback validation when none is configured mandatory."
  [:person/first-name
   :person/last-name
   :person/universal-email-address])

(def static-fields
  {:entity.type/person {:before person-name-and-email-attributes
                        :set (set person-name-and-email-attributes)}})

(defn protected-creator-field?
  [entity-type attribute]
  (contains? (get-in static-fields [entity-type :set]) attribute))

(defn protected-fields-first
  [{:keys [entity-type fields]}]
  (let [protected-order (get-in static-fields [entity-type :before])
        protected-set (set protected-order)
        fields-by-attribute (->> fields
                                 (map (fn [{:keys [attribute] :as field-config}]
                                        [attribute field-config]))
                                 (into {}))
        protected-fields (keep fields-by-attribute protected-order)
        other-fields (remove (fn [{:keys [attribute]}]
                               (contains? protected-set attribute))
                             fields)]
    (into (vec protected-fields) other-fields)))

(defn protect-field-visibility
  [field-config]
  (cond-> field-config
    (contains? field-config :create)
    (update-in [:create :setting]
               (fn [setting]
                 (if (= :not-shown setting)
                   :optional
                   setting)))

    (contains? field-config :stages)
    (update :stages
            (fn [stages]
              (->> stages
                   (map (fn [[stage-id stage-config]]
                          [stage-id
                           (update stage-config :setting
                                   (fn [setting]
                                     (if (= :not-shown setting)
                                       :optional
                                       setting)))]))
                   (into {}))))))

(defn ensure-protected-fields
  [{:keys [config config-fn entity-type]}]
  (let [protected-order (get-in static-fields [entity-type :before])
        configured-attributes (->> (:fields config)
                                   (map :attribute)
                                   set)
        missing-fields (->> protected-order
                            (remove configured-attributes)
                            (mapv (fn [attribute]
                                    (config-fn attribute :optional))))
        config (cond-> config
                 (seq missing-fields)
                 (update :fields (fn [fields]
                                   (into missing-fields fields))))]
    (update config :fields
            (fn [fields]
              (->> (protected-fields-first {:entity-type entity-type
                                            :fields fields})
                   (mapv (fn [{:keys [attribute] :as field-config}]
                           (cond-> field-config
                             (protected-creator-field? entity-type attribute)
                             protect-field-visibility))))))))

(def custom-validations
  "Per-entity-type cross-field validations that don't fit the per-field
   mandatoriness model. Each rule declares the participating attributes plus
   a `:kind` the rule-runner knows how to evaluate.

   Currently only `:at-least-one`: at least one of the listed attributes must
   be non-blank.

   The same data drives the creator's `m/entity-validations` chain (so the
   confirm button is gated correctly) and the stage-entry-criteria validator
   (so the modal surfaces all participating fields when the group fails)."
  {:entity.type/person
   [{:kind :at-least-one
     :attributes person-name-and-email-attributes
     :error-key :custom-error/insufficient-person-info}]})

(defn failed-custom-validation?
  "True iff the `rule` (one entry from `custom-validations`) is failing for
   `entity`. Pure: no re-frame, no Datomic — just the entity-map and the
   rule shape."
  [{:keys [kind attributes]} entity]
  (case kind
    :at-least-one (every? cu/blank-value?
                          (map (fn [attr] (get entity attr))
                               attributes))
    ;; Unknown rule kind: degrade to "not failing" rather than throwing.
    (do (println "Unkonwn custom-validation kind " kind)
        false)))

(defn mandatory-config-setting?
  [{:keys [field-config stage-id]}]
  (= :mandatory
     (if stage-id
       (get-in field-config [:stages stage-id :setting])
       (get-in field-config [:create :setting]))))

(defn rule-has-mandatory-field?
  [{:keys [rule fields-config stage-id]}]
  (let [rule-attributes (set (:attributes rule))]
    (->> (:fields fields-config)
         (filter (fn [{:keys [attribute]}]
                   (contains? rule-attributes attribute)))
         (some (fn [field-config]
                 (mandatory-config-setting? {:field-config field-config
                                             :stage-id stage-id})))
         boolean)))

(defn failed-custom-validations
  "Returns the custom-validation rules currently failing for an entity.
   Group fallback rules are suppressed when one of their fields has an
   explicit mandatory setting, because normal field validation then provides
   the more specific error."
  ([entity-type entity]
   (failed-custom-validations entity-type entity nil))
  ([entity-type entity {:keys [fields-config stage-id]}]
   (->> (get custom-validations entity-type)
        (remove (fn [rule]
                  (and fields-config
                       (rule-has-mandatory-field? {:rule rule
                                                   :fields-config fields-config
                                                   :stage-id stage-id}))))
        (filter (fn [rule]
                  (failed-custom-validation? rule entity))))))

(defn field-handled-elsewhere?
  [{:keys [entity-type]} [k _]]
  (contains? (get fields-handled-elsewhere entity-type)
             k))

(defn attachment-field?
  [{:field/keys [entity-types]}]
  (= entity-types [:entity.type/attachment]))

(defn check-field?
  [{:field/keys [entity-types]}]
  (or (= entity-types [:entity.type/pre-investment-check])
      (= entity-types [:entity.type/pre-exit-check])))

(defn risk-field?
  [{:field/keys [entity-types]}]
  (= entity-types [:entity.type/risk-holding]))

(def allowed-data-types
  #{:data.type/boolean
    :data.type/currency-symbol
    :data.type/currency-value
    :data.type/date
    :data.type/datetime
    :data.type/email
    ;;:data.type/financials
    ;;:data.type/id
    :data.type/integer
    :data.type/keyword
    :data.type/keyword-multi
    ;;:data.type/long
    :data.type/multiplier
    :data.type/number
    :data.type/percentage
    :data.type/ref-multi
    :data.type/ref-single
    ;;:data.type/tesi-financials
    ;;:data.type/tesi-financials-2
    :data.type/text
    ;;:data.type/text-edn
    :data.type/text-multi
    :data.type/text-multiline
    :data.type/text-richtext
    :data.type/text-simple
    :data.type/text-url})

(defn hidden?
  [[_k {:field/keys [hidden-from-configuration? default-authorization data-type
                     calculated-field composite-attributes secondary-field?]
        :keys [synthetic?]
        :as field-schema}]]
  (or (attachment-field? field-schema)
      (check-field? field-schema)
      (risk-field? field-schema)
      (not (contains? allowed-data-types data-type))
      (and (= data-type :data.type/currency-symbol)
           secondary-field?)
      hidden-from-configuration?
      calculated-field
      (seq composite-attributes)
      (-> default-authorization :access/write (= :role/system))
      synthetic?))

;; removing explicitly - should probably be handled on entity-schema level
(def system-fields
  #{:common/created-on
    :common/archived?
    :common/disabled?
    :common/related-entities
    :common/team-members})

(defn hide?
  [{:keys [entity-type pipeline field-configs
           tenant-modules] :as _ctx}
   [attribute {:field/keys [default-authorization default-visibility
                            required-modules secondary-field?] :as _fs}]]
  (let [min-zadmin-required (and (-> default-authorization
                                     :access/read
                                     (roles/zadmin?))
                                 (-> default-authorization
                                     :access/write
                                     (roles/zadmin?)))
        named-value? (and (= entity-type :entity.type/named-value)
                          (= attribute :named-value/value))
        system-field? (contains? system-fields attribute)
        default-field-visibility (:field-visibility default-visibility)
        tenant-field-access-config (get-in field-configs
                                           [entity-type attribute])
        tenant-field-config (:field-settings/in-use-config tenant-field-access-config)
        field-setting (or tenant-field-config default-field-visibility
                          :not-in-use)
        inactive-for-tenant? (contains? field-permissions/NEGATIVE-IN-USE-VALUES
                                        field-setting)
        pipeline-setting (when (and (not inactive-for-tenant?)
                                    pipeline)
                           (some-> pipeline
                                   :pipeline/settings
                                   (get-in [entity-type attribute
                                            :field-settings/in-use-config])))
        pipeline-setting (or pipeline-setting field-setting)
        inactive-for-pipeline? (contains? field-permissions/NEGATIVE-IN-USE-VALUES
                                          pipeline-setting)
        missing-module? (not-every? (set tenant-modules #_@session/tenant-modules) required-modules)]
    ;; debug:
    #_(when (= attribute :investor/preferred-currency)
        (prn ">pref-cur" (keys field-configs) entity-type
             tenant-field-config inactive-for-tenant? pipeline-setting
             inactive-for-pipeline? missing-module?
             tenant-field-access-config entity-type))
    (and (not named-value?)
         (or missing-module?
             system-field?
             min-zadmin-required
             secondary-field?
             #_(and (not (session/zadmin?))
                    (some #{attribute} fields-hidden-from-TA))
             inactive-for-tenant?
             inactive-for-pipeline?))))

;; Customization required for some fields - leaving actual schema untouched
;; in case other places rely on it:
(defn customize-field-schema
  [{:keys [entity-type]} [k v]]
  [k (case [entity-type k]
       [:entity.type/person :person/universal-email-address]
       ;; Create-schema customization: person email is selectable in create
       ;; flows without inheriting the legacy mandatory metadata from the base
       ;; schema. This is not a runtime mandatory consumer.
       (-> v
           (dissoc :field/mandatory)
           (update :field/constraints
                   dissoc :constraint/string-min-length))

       [:entity.type/holding :holding/name]
       (-> v
           (assoc-in [:field/default-visibility :editor]
                     :editor.type/text-simple)
           (update :field/constraints
                   ;; already covered by being mandatory:
                   dissoc :constraint/string-min-length))

       [:entity.type/sale-agreement :sale-agreement/name]
       (-> v
           (update :field/constraints
                   ;; already covered by being mandatory:
                   dissoc :constraint/string-min-length))

       [:entity.type/fundraising-opportunity :fundraising-opportunity/opportunity-name]
       (-> v
           (update :field/constraints
                   ;; already covered by being mandatory:
                   dissoc :constraint/string-min-length))

       [:entity.type/oppo :oppo/name]
       (-> v
           (update :field/constraints
                   ;; already covered by being mandatory:
                   dissoc :constraint/string-min-length))

       [:entity.type/sales-opportunity :sales-opportunity/name]
       (-> v
           (update :field/constraints
                   ;; already covered by being mandatory:
                   dissoc :constraint/string-min-length))

       [:entity.type/exit-opportunity :exit-opportunity/name]
       (-> v
           (update :field/constraints
                   ;; already covered by being mandatory:
                   dissoc :constraint/string-min-length))

       [:entity.type/company :asset/name]
       (-> v
           (update :field/constraints
                   ;; already covered by being mandatory:
                   dissoc :constraint/string-min-length))

       [:entity.type/fof-asset :asset/name]
       (-> v
           (update :field/constraints
                   ;; already covered by being mandatory:
                   dissoc :constraint/string-min-length))

       [:entity.type/real-estate :asset/name]
       (-> v
           (update :field/constraints
                   ;; already covered by being mandatory:
                   dissoc :constraint/string-min-length))

       [:entity.type/other-asset :asset/name]
       (-> v
           (update :field/constraints
                   ;; already covered by being mandatory:
                   dissoc :constraint/string-min-length))

       (cond (and (= (:field/data-type v) :data.type/email)
                  (= (:field/constraints v) []))
             (-> v
                 (assoc :field/constraints
                        {:constraint/valid-email true}))

             :else
             v))])

(defn configurable-creator-fields
  [ctx entity-type]
  (let [exclude-attributes (set (:exclude-attributes ctx))]
    (->> (all-fields ctx entity-type)
         (remove hidden?)
         (remove (partial hide? (assoc ctx :entity-type entity-type)))
         (remove (partial field-handled-elsewhere?
                          {:entity-type entity-type}))
         (remove (fn [[k _]]
                   (contains? exclude-attributes k)))
         (mapv (partial customize-field-schema (assoc ctx :entity-type entity-type)))
         (into {}))))

(defn mandatory-fields
  [ctx entity-type]
  (->> (configurable-creator-fields ctx entity-type)
       (filterv mandatory?)
       (into {})))

(defn default-config
  [{:keys [config-fn entity-type]}]
  (cond (= entity-type :entity.type/oppo)
        ;;TODO read from config file etc - defined by PO
        {:fields
         [(config-fn :oppo/name :mandatory)
          (config-fn :oppo/owner :mandatory)
          (config-fn :oppo/lead-source-v2 :mandatory)
          (config-fn :oppo/lead-type-v2 :optional)]}

        (= entity-type :entity.type/company)
        {:fields
         [(config-fn :asset/name :mandatory)
          (config-fn :asset/description :optional)
          (config-fn :company/website :optional)
          (config-fn :common/network-drive :optional)]}

        (= entity-type :entity.type/fof-asset)
        {:fields
         [(config-fn :asset/name :mandatory)
          (config-fn :fof-asset/gp :optional)
          (config-fn :fof-asset/target-asset-class :optional)
          (config-fn :fof-asset/commitment-period-term :optional)
          (config-fn :fof-asset/datetime-fundraising-until :optional)]}

        (= entity-type :entity.type/real-estate)
        {:fields
         [(config-fn :asset/name :mandatory)
          (config-fn :asset/country :optional)
          (config-fn :asset/city :optional)
          (config-fn :asset/street-address :optional)
          (config-fn :real-estate/property-type :optional)
          (config-fn :real-estate/broker :optional)]}

        (= entity-type :entity.type/other-asset)
        {:fields
         [(config-fn :asset/name :mandatory)
          (config-fn :other-asset/type :optional)
          (config-fn :other-asset/tag-tags :optional)
          (config-fn :asset/description :optional)
          (config-fn :asset/country :optional)
          (config-fn :asset/city :optional)
          (config-fn :asset/street-address :optional)]}

        (= entity-type :entity.type/named-value)
        {:fields
         [(config-fn :named-value/value :mandatory)]}

        (= entity-type :entity.type/person)
        {:fields
         [(config-fn :person/universal-email-address :optional)
          (config-fn :person/first-name :optional)
          (config-fn :person/last-name :optional)
          (config-fn :person/universal-title :optional)
          (config-fn :person/universal-phone-number :optional)
          (config-fn :person/mobile-phone-number :optional)
          (config-fn :person/work-phone-number :optional)
          (config-fn :person/work-email-address :optional)
          (config-fn :person/other-email-addresses :optional)
          (config-fn :person/location-city :optional)
          (config-fn :person/location-country :optional)
          (config-fn :person/linkedin-url :optional)
          (config-fn :person/notes :optional)
          (config-fn :person/tag-tags :optional)]}

        (= entity-type :entity.type/holding)
        {:fields
         [(config-fn :holding/name :mandatory)
          (config-fn :holding/owner :mandatory)
          (config-fn :holding/target-asset :mandatory)
          (config-fn :holding/priority :optional)
          (config-fn :holding/cur-paid-in-capital :optional)
          (config-fn :holding/cur-distributions :optional)
          (config-fn :holding/cur-residual-value :optional)
          (config-fn :holding/exit-plan :optional)
          (config-fn :holding/recent-status :optional)
          (config-fn :holding/exit-status :optional)]}

        (= entity-type :entity.type/investor)
        {:fields
         [(config-fn :investor/investor-type :optional)
          (config-fn :investor/owner :optional)
          (config-fn :investor/description :optional)
          (config-fn :investor/preferred-currency :optional)
          (config-fn :investor/type :optional)
          (config-fn :investor/language :optional)
          (config-fn :investor/investor-types :optional)]}

        (= entity-type :entity.type/fund-commitment)
        {:fields
         [(config-fn :fund-commitment/investor :mandatory)
          (config-fn :fund-commitment/cur-committed-amount :mandatory)
          (config-fn :fund-commitment/closing-information :mandatory)
          (config-fn :fund-commitment/owner :optional)
          (config-fn :fund-commitment/fund :optional)
          (config-fn :fund-commitment/committed-date :optional)
          (config-fn :fund-commitment/won-by :optional)
          (config-fn :fund-commitment/credit :optional)]}

        (= entity-type :entity.type/sale-agreement)
        {:fields
         [(config-fn :sale-agreement/name :mandatory)
          (config-fn :sale-agreement/owner :mandatory)
          (config-fn :sale-agreement/customer :mandatory)
          (config-fn :sale-agreement/credit :optional)
          (config-fn :sale-agreement/won-by :optional)
          (config-fn :sale-agreement/overview :optional)
          (config-fn :sale-agreement/customer-legal-id :optional)
          (config-fn :sale-agreement/customer-number :optional)
          (config-fn :sale-agreement/committed-date :optional)
          (config-fn :sale-agreement/cur-committed-amount :optional)
          (config-fn :sale-agreement/contract-services :optional)
          (config-fn :sale-agreement/contract-end-date :optional)]}

        (= entity-type :entity.type/fundraising-opportunity)
        {:fields
         [(config-fn :fundraising-opportunity/opportunity-name :mandatory)
          (config-fn :fundraising-opportunity/owner :mandatory)
          (config-fn :fundraising-opportunity/probability-category :optional)
          (config-fn :fundraising-opportunity/closing-stage :optional)
          (config-fn :fundraising-opportunity/cur-min-commitment-amount :optional)
          (config-fn :fundraising-opportunity/cur-max-commitment-amount :optional)
          (config-fn :fundraising-opportunity/needs-feeder-fund? :optional)]}

        (= entity-type :entity.type/sales-opportunity)
        {:fields
         [(config-fn :sales-opportunity/name :mandatory)
          (config-fn :sales-opportunity/owner :optional)
          (config-fn :sales-opportunity/lead-source :optional)
          (config-fn :sales-opportunity/probability-category :optional)]}

        (= entity-type :entity.type/exit-opportunity)
        {:fields
         [(config-fn :exit-opportunity/name :mandatory)
          (config-fn :exit-opportunity/owner :mandatory)
          (config-fn :exit-opportunity/datetime-expected-closing-date :optional)
          (config-fn :exit-opportunity/cur-expected-EV :optional)
          (config-fn :exit-opportunity/probability-percentage :optional)]}))

;; note: currently only investor target-profile. update if needed:
(defn target-profile?
  [{:keys [pipeline entity-type target-type]}]
  (let [froppo-investor? (and (= entity-type :entity.type/fundraising-opportunity)
                              target-type
                              pipeline)
        investor-only? (= (set (:pipeline/target-asset-types pipeline))
                          #{:entity.type/investor})
        sales-oppo-investor? (and (= entity-type :entity.type/sales-opportunity)
                                  target-type
                                  investor-only?)
        exit-oppo-investor? (and (= entity-type :entity.type/exit-opportunity)
                                 target-type
                                 pipeline)
        investor-target-profile? (or froppo-investor?
                                     sales-oppo-investor?
                                     exit-oppo-investor?)]
    investor-target-profile?))

;; update if other cases where target-profile
(defn resolve-target-profile-type
  [{:keys [entity-type] :as args}]
  (when (target-profile? args)
    (case entity-type
      :entity.type/fundraising-opportunity :entity.type/investor
      :entity.type/sales-opportunity :entity.type/investor
      :entity.type/exit-opportunity :entity.type/investor
      nil)))

;; returns
;; [main-entity-config-path target-entity-config-path associations-config-path]
(defn resolve-config-paths
  [{:keys [entity-type target-type pipeline]}]
  (cond
    ;;target-asset or target-customer + pipeline:
    ;; TODO: customer can be person, company, or investor;
    ;;       investor can be person or company <- separate also these?
    (and target-type entity-type pipeline)
    (cond-> {:main-config-path [entity-type target-type :main-config]
             :target-config-path [entity-type target-type :target-config]
             :associations-config-path [entity-type target-type :associations-config]}
      (target-profile? {:pipeline pipeline
                        :entity-type entity-type
                        :target-type target-type})
      (assoc :target-profile-config-path [entity-type target-type :target-profile-config]))

    ;;holding + target-asset; no pipeline
    (and target-type entity-type)
    {:main-config-path [entity-type target-type :main-config]
     :target-config-path [entity-type target-type :target-config]
     :associations-config-path [entity-type target-type :associations-config]}

    ;;no target
    (and entity-type pipeline)
    {:main-config-path [entity-type :single-entity-config]
     :associations-config-path [entity-type :associations-config]}

    ;;no pipeline, no target-asset/customer
    entity-type
    {:main-config-path [entity-type :single-entity-config]
     :associations-config-path [entity-type :associations-config]}))

(defn resolve-stage-config
  [{:keys [system-mandatory field-config stages]}]
  (let [{:keys [attribute] stages-config :stages} field-config
        system-mandatory? (contains? system-mandatory attribute)
        ;;can system mandatory be set as hidden-with-default-value ?
        ;;system-mandatory overrides potential user-definitions
        stage-ids (->> stages
                       (mapv :db/id))
        stage-ids-with-inherit (->> stage-ids
                                    ;;first stage; represent no inheritance with nil:
                                    (into [nil])
                                    (partition 2 1))
        resolved-config (->> stage-ids-with-inherit
                             (reduce (fn [config [inherit-from current-id]]
                                       (assoc
                                        config
                                        current-id
                                        (cond
                                          system-mandatory?
                                          {:setting :mandatory
                                           :system-mandatory? true
                                           :non-editable? true}

                                          ;; if previous stage's config is mandatory
                                          ;; => inherit & override
                                          (= (get-in config
                                                     [inherit-from :setting])
                                             :mandatory)
                                          {:setting :mandatory
                                           :non-editable? true
                                           ;; inherited from an earlier stage's config
                                           :inherited? true}

                                          :else
                                          (let [user-config (get-in
                                                             config
                                                             [current-id :setting])]
                                            (cond->
                                             {:setting
                                              (or user-config :optional)}
                                              (nil? user-config)
                                              (assoc :default? true))))))
                                     stages-config))]
    resolved-config))

(defn resolve-fields-stage-config
  [{:keys [system-mandatory config stages]}]
  (update
   config
   :fields
   (fn [fields]
     (->> fields
          (mapv (fn [field-config]
                  (assoc field-config
                         :stages
                         (resolve-stage-config
                          {:field-config field-config
                           :system-mandatory system-mandatory
                           :stages stages}))))))))

(defn resolve-create-config
  [{:keys [system-mandatory field-config]}]
  (let [{:keys [attribute] create-config :create} field-config
        system-mandatory? (contains? system-mandatory attribute)
        current-setting (get create-config :setting)
        resolved-config (cond
                          system-mandatory?
                          {:setting :mandatory
                           :system-mandatory? true
                           :non-editable? true}

                          ;; TODO check if current-setting is not a valid value
                          (nil? current-setting)
                          {:setting :optional
                           :default? true}

                          :else
                          {:setting current-setting})]
    resolved-config))

(defn resolve-fields-create-config
  [{:keys [system-mandatory config]}]
  (update
   config
   :fields
   (fn [fields]
     (->> fields
          (mapv (fn [field-config]
                  (assoc field-config
                         :create
                         (resolve-create-config
                          {:system-mandatory system-mandatory
                           :field-config field-config}))))))))

(defn resolve-association-stage-config
  [{:keys [association-config stages]}]
  (let [{stages-config :stages} association-config
        stage-ids (->> stages
                       (mapv :db/id))
        stage-ids-with-inherit (->> stage-ids
                                    ;;first stage; represent no inheritance with nil:
                                    (into [nil])
                                    (partition 2 1))
        resolved-config (->> stage-ids-with-inherit
                             (reduce (fn [config [inherit-from current-id]]
                                       (assoc
                                        config
                                        current-id
                                        (cond
                                          ;; if previous stage's config is mandatory
                                          ;; => inherit & override
                                          (= (get-in config
                                                     [inherit-from :setting])
                                             :mandatory)
                                          {:setting :mandatory
                                           :non-editable? true
                                           ;; inherited from an earlier stage's config
                                           :inherited? true}

                                          :else
                                          (let [user-config (get-in
                                                             config
                                                             [current-id :setting])]
                                            (cond->
                                             {:setting
                                              (or user-config :optional)}
                                              (nil? user-config)
                                              (assoc :default? true))))))
                                     stages-config))]
    resolved-config))

(defn resolve-associations-stage-config
  [{:keys [config stages]}]
  (update
   config
   :associations
   (fn [associations]
     (->> associations
          (mapv (fn [association-config]
                  (assoc association-config
                         :stages
                         (resolve-association-stage-config
                          {:association-config association-config
                           :stages stages}))))))))

(defn resolve-prevent-stage-config
  [{:keys [config stages]}]
  (update-in
   config
   [:prevent-create? :stages]
   (fn [stages-config]
     (let [;;stages-config (:stages prevent-create?)
           stage-ids (->> stages
                          (sort-by :common/ord)
                          (mapv :db/id))
           first-stage-id (first stage-ids)
           stage-ids-with-inherit (->> stage-ids
                                       ;;first stage; represent no inheritance with nil:
                                       (into [nil])
                                       (partition 2 1))
           resolved-config (->> stage-ids-with-inherit
                                (reduce (fn [config [inherit-from current-id]]
                                          (assoc
                                           config
                                           current-id
                                           (cond
                                             ;; at least one (i.e. first) stage must
                                             ;; allow creation:
                                             (= first-stage-id current-id)
                                             {:setting :allow
                                              :non-editable? true}
                                             ;; if previous stage's config is :prevent
                                             ;; => inherit & override
                                             (= (get-in config
                                                        [inherit-from :setting])
                                                :prevent)
                                             {:setting :prevent
                                              :non-editable? true
                                              ;; inherited from an earlier stage's config
                                              :inherited? true}

                                             :else
                                             (let [user-config (get-in
                                                                config
                                                                [current-id :setting])]
                                               (cond->
                                                {:setting
                                                 (or user-config :allow)}
                                                 (nil? user-config)
                                                 (assoc :default? true))))))
                                        stages-config))]
       resolved-config))))

(defn toggle-prevent-create-config
  "Flip one stage's :prevent-create? setting (:allow <-> :prevent) within a
   pipeline's parsed (not yet stage-resolved) creator-config, returning the
   updated config. Shared by every UI surface that can author this setting -
   see the pipeline-stages curtain and the entity-creator's field-requirements
   table header."
  [config stage-id]
  (update-in config [:prevent-create? :stages stage-id :setting]
             (fn [setting]
               (if (= setting :prevent)
                 :allow
                 :prevent))))

(defn pipeline-required?
  [{:keys [entity-type]}]
  ;;TODO not finished:
  (contains? #{:entity.type/oppo
               :entity.type/sales-opportunity
               :entity.type/fundraising-opportunity
               :entity.type/exit-opportunity}
             entity-type))

(defn target-required?
  [{:keys [entity-type]}]
  ;;TODO not finished:
  (contains? #{:entity.type/oppo
               ;;:entity.type/holding
               ;; should investor be always required?:
               ;;:entity.type/fundraising-opportunity
               ;;^update: froppo-creation flow will be more comprehensive
               ;; can we create without target-customer?:
               :entity.type/sales-opportunity}
             entity-type))
